// js/common/collection/collection-infinite-scroll.js
//
// Progressive enhancement for Shopify collection pagination. The server always renders standard,
// crawlable ?page=N pagination (see snippets/pagination.liquid) plus a real <a> "Load more" link
// (see snippets/infinite-scroll-controls.liquid) - both work with JavaScript disabled. When this
// module runs, it intercepts that same next-page link to fetch and append products automatically
// as the visitor nears the bottom of the grid, using Shopify's Section Rendering API so results
// stay in sync with any active filters/sort. Filters/sort changes are handled by the theme's own
// facets-listeners.js, which fully replaces this section's DOM (a fresh page 1) - this element's
// disconnectedCallback/connectedCallback cycle then resets infinite-scroll state for free.

const ROOT_MARGIN_PX = 700; // 600-900px before the sentinel enters the viewport, per spec

class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector("product-list");
    this.sentinel = this.querySelector("[data-infinite-scroll-sentinel]");
    this.controls = this.querySelector(".infinite-scroll-controls");
    this.statusRegion = this.querySelector("[data-infinite-scroll-status]");
    this.loadMoreButton = this.querySelector("[data-load-more-button]");
    this.completeMessage = this.querySelector("[data-infinite-scroll-complete]");
    this.pagination = this.querySelector(".pagination");

    // Translated strings, rendered server-side by snippets/infinite-scroll-controls.liquid via
    // the theme's own `t` filter - never hardcoded here, so this stays translation-ready even
    // though only the default locale is populated today (matching the rest of this project).
    this.i18n = {
      loading: this.controls?.dataset.i18nLoading || "",
      error: this.controls?.dataset.i18nError || "",
      countOne: this.controls?.dataset.i18nCountOne || "",
      countOther: this.controls?.dataset.i18nCountOther || ""
    };

    if (!this.list) {
      return;
    }

    this.sectionId = this.getAttribute("section-id");
    this.nextUrl = this.loadMoreButton ? this.loadMoreButton.getAttribute("href") : null;
    this.loading = false;
    this.exhausted = !this.nextUrl;
    this.abortController = null;
    this.loadedHandles = new Set(
      Array.from(this.list.querySelectorAll("product-card[handle]"), (card) => card.getAttribute("handle"))
    );

    this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
    this.loadMoreButton?.addEventListener("click", this.handleLoadMoreClick);

    if (this.sentinel && this.nextUrl && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        rootMargin: `0px 0px ${ROOT_MARGIN_PX}px 0px`,
        threshold: 0
      });
      this.observer.observe(this.sentinel);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
    this.loadMoreButton?.removeEventListener("click", this.handleLoadMoreClick);
  }

  handleIntersect(entries) {
    if (entries.some((entry) => entry.isIntersecting)) {
      this.loadNextPage();
    }
  }

  handleLoadMoreClick(event) {
    event.preventDefault();
    this.loadNextPage();
  }

  async loadNextPage() {
    if (this.loading || this.exhausted || !this.nextUrl) {
      return;
    }

    this.loading = true;
    this.setLoadingState(true);
    this.announce(this.i18n.loading);

    this.abortController = new AbortController();

    let url;
    try {
      url = new URL(this.nextUrl, window.location.origin);
    } catch (error) {
      this.handleLoadError();
      return;
    }
    url.searchParams.set("section_id", this.sectionId);

    try {
      const response = await fetch(url.toString(), { signal: this.abortController.signal });

      if (!response.ok) {
        throw new Error(`Infinite scroll request failed with status ${response.status}`);
      }

      const html = await response.text();
      this.appendPage(html);
    } catch (error) {
      if (error.name !== "AbortError") {
        this.handleLoadError();
      }
    } finally {
      this.loading = false;
      this.setLoadingState(false);
    }
  }

  appendPage(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newList = doc.querySelector("product-list");
    const newPagination = doc.querySelector(".pagination");
    const newNextLink = doc.querySelector('[data-load-more-button]');

    if (!newList) {
      this.handleLoadError();
      return;
    }

    const fragment = document.createDocumentFragment();
    let addedCount = 0;

    Array.from(newList.children).forEach((card) => {
      const handle = card.getAttribute && card.getAttribute("handle");

      // Skip anything already on the page - prevents duplicate cards if a fetch is retried
      // or a product shifts between pages while the visitor is scrolling.
      if (handle) {
        if (this.loadedHandles.has(handle)) {
          return;
        }
        this.loadedHandles.add(handle);
      }

      card.querySelectorAll("img").forEach((img) => {
        // The fetched page is rendered as if it were page 1 of its own paginate loop, so the
        // theme's own "eager load the first 3 cards" logic re-triggers on every fetch. These
        // cards are always below the fold once appended, so force them lazy regardless.
        img.loading = "lazy";
        if (img.fetchPriority) {
          img.fetchPriority = "low";
        }
      });

      card.classList.add("collection-infinite-scroll__card--entering");
      fragment.appendChild(card);
      addedCount += 1;
    });

    this.list.appendChild(fragment);

    requestAnimationFrame(() => {
      this.list.querySelectorAll(".collection-infinite-scroll__card--entering").forEach((card) => {
        card.classList.remove("collection-infinite-scroll__card--entering");
      });
    });

    // Swap in the freshly-fetched pagination nav so its rel="prev"/rel="next" links and current-page
    // indicator stay accurate for crawlers/no-JS users, then read the new next-page link from it.
    if (this.pagination && newPagination) {
      this.pagination.replaceWith(newPagination);
      this.pagination = newPagination;
    }

    this.updateHistory(newPagination);

    if (newNextLink) {
      this.nextUrl = newNextLink.getAttribute("href");
      // Keep the visible fallback link's href in sync with real state, not just the
      // internal variable - it must stay accurate for right-click/copy-link/new-tab and
      // as the genuine next-page target if a later fetch fails.
      if (this.loadMoreButton) {
        this.loadMoreButton.href = this.nextUrl;
      }
      if (this.observer && this.sentinel) {
        this.observer.disconnect();
        this.observer.observe(this.sentinel);
      }
    } else {
      this.nextUrl = null;
      this.exhausted = true;
      this.observer?.disconnect();
      this.showComplete();
    }

    if (addedCount > 0) {
      const message = addedCount === 1
        ? this.i18n.countOne
        : this.i18n.countOther.replace("ISC_COUNT", String(addedCount));
      this.announce(message);
    }

    this.dispatchEvent(
      new CustomEvent("infinite-scroll:page-loaded", {
        bubbles: true,
        detail: { url: window.location.href, addedCount }
      })
    );
  }

  updateHistory(newPagination) {
    const currentPageEl = newPagination?.querySelector('[aria-current="page"]');
    const currentPage = currentPageEl ? currentPageEl.textContent.trim() : null;

    const url = new URL(window.location.href);
    if (currentPage) {
      url.searchParams.set("page", currentPage);
    }

    // pushState (not replaceState): each loaded page becomes its own history entry, so the
    // back button steps through them. Nothing in the DOM is removed on "back" - the browser
    // simply restores the URL and its own remembered scroll offset for that entry, leaving the
    // already-appended products, active filters and sort order untouched.
    window.history.pushState({ infiniteScroll: true }, "", url.toString());
  }

  setLoadingState(isLoading) {
    if (this.loadMoreButton) {
      this.loadMoreButton.setAttribute("aria-disabled", String(isLoading));
      this.loadMoreButton.classList.toggle("is-loading", isLoading);
    }
    this.classList.toggle("is-loading", isLoading);
  }

  showComplete() {
    if (this.loadMoreButton) {
      this.loadMoreButton.setAttribute("hidden", "");
    }
    if (this.completeMessage) {
      this.completeMessage.removeAttribute("hidden");
      this.announce(this.completeMessage.textContent.trim());
    }
  }

  handleLoadError() {
    // Fail safe: never hide the fallback. The real next-page link (this.nextUrl) is left
    // untouched, so the visible "Load more" button - and the always-present standard
    // pagination below it - both keep working as plain navigation.
    this.observer?.disconnect();
    if (this.loadMoreButton) {
      this.loadMoreButton.removeAttribute("hidden");
      this.loadMoreButton.classList.remove("is-loading");
      this.loadMoreButton.setAttribute("aria-disabled", "false");
    }
    this.announce(this.i18n.error);
  }

  announce(message) {
    if (this.statusRegion) {
      this.statusRegion.textContent = message;
    }
  }
}

if (!window.customElements.get("collection-infinite-scroll")) {
  window.customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
}
